/* ============================================================
   THE FUDGE PIE CO  ·  brand motion
   ------------------------------------------------------------
   The only JavaScript VBO adds to Horizon.

   Horizon already ships scroll reveal, view transitions, cart
   animations, galleries and accordions, so none of that is
   duplicated here. This file carries exactly one thing the
   approved motion board asked for that Horizon has no equivalent
   of: the cursor-reactive paper-cut tilt.

   Written as a custom element rather than a document-wide
   listener so it survives the theme editor. Blocks get added,
   reordered and deleted there without a page reload, and a
   global querySelectorAll on load would silently stop working
   the first time a merchant moves a section.

   Bob 2026-07-30
   ============================================================ */

(() => {
  'use strict';

  // Guard: the theme editor can evaluate this file more than once.
  if (customElements.get('fudge-tilt')) return;

  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  /**
   * <fudge-tilt> gives its contents a subtle paper-cut lift that follows
   * the pointer. Rotation is written as custom properties so the actual
   * animation stays in CSS on the compositor.
   *
   * Attributes:
   *   max - furthest rotation in degrees, default 6
   */
  class FudgeTilt extends HTMLElement {
    connectedCallback() {
      // Coarse pointers have no hover, and a tilt that only fires on tap
      // reads as a glitch rather than a flourish.
      this.fine = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.max = parseFloat(this.getAttribute('max') || '6');

      this.onMove = this.onMove.bind(this);
      this.onLeave = this.onLeave.bind(this);
      this.sync = this.sync.bind(this);

      REDUCED.addEventListener('change', this.sync);
      this.fine.addEventListener('change', this.sync);
      this.sync();
    }

    disconnectedCallback() {
      // Theme editor churn: always leave the element as we found it.
      this.detach();
      REDUCED.removeEventListener('change', this.sync);
      this.fine.removeEventListener('change', this.sync);
    }

    /** Attach or detach based on current motion and pointer preferences. */
    sync() {
      if (REDUCED.matches || !this.fine.matches) {
        this.detach();
        this.reset();
      } else {
        this.attach();
      }
    }

    attach() {
      if (this.attached) return;
      this.addEventListener('pointermove', this.onMove);
      this.addEventListener('pointerleave', this.onLeave);
      this.attached = true;
    }

    detach() {
      if (!this.attached) return;
      this.removeEventListener('pointermove', this.onMove);
      this.removeEventListener('pointerleave', this.onLeave);
      this.attached = false;
    }

    onMove(event) {
      // One read per frame. Reading geometry on every pointermove is the
      // classic way to make a nice effect feel cheap on a mid-range phone.
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        const box = this.getBoundingClientRect();
        if (!box.width || !box.height) return;

        // -0.5 .. 0.5 from the centre of the element.
        const px = (event.clientX - box.left) / box.width - 0.5;
        const py = (event.clientY - box.top) / box.height - 0.5;

        // Pointer right tips the right edge away, pointer down tips the
        // bottom towards the viewer. That is the direction that reads as
        // a sheet of paper lifting rather than a box rotating.
        this.style.setProperty('--fp-ry', `${(px * this.max).toFixed(2)}deg`);
        this.style.setProperty('--fp-rx', `${(-py * this.max).toFixed(2)}deg`);
      });
    }

    onLeave() {
      this.reset();
    }

    reset() {
      if (this.frame) {
        cancelAnimationFrame(this.frame);
        this.frame = null;
      }
      this.style.setProperty('--fp-rx', '0deg');
      this.style.setProperty('--fp-ry', '0deg');
    }
  }

  customElements.define('fudge-tilt', FudgeTilt);
})();
